/* eslint-disable react/display-name */
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, Document, Inline, INLINES, Text } from '@contentful/rich-text-types';
import { css } from '@emotion/react';
import styled, { Interpolation } from '@emotion/styled';
import { Asset, Entry } from 'contentful';
import Link from 'next/link';
import React, { ReactNode, useContext } from 'react';
import { IScribble } from '../../../types/@types/generated/contentful';
import { colors, spacing } from '../../../utils/styleguide';
import Icon from '../../component-library/Icon';
import StaticPageLink from '../../component-library/navigation/LegacyStaticPageLink';
import Scribble, { ScribbleVariants } from '../../component-library/text/Scribble';
import Typography, { styles } from '../../component-library/text/Typography';
import { TemplatingContext } from '../contexts/TemplatingContext';
import ResponsiveBlock from './components/ResponsiveBlock';

export const InlineImage = styled.img`
  width: 100%;
`;

type StyledLinkProps = { linkColor: string | undefined; underlineLinks: boolean | undefined };

const StyledLink = styled.a`
  color: ${(props: StyledLinkProps) => props.linkColor};
  text-decoration: ${(props: StyledLinkProps) => (props.underlineLinks ? 'underline' : 'none')};
`;

let responsiveBlockIndex = 0;

const boldCss = css`
  b {
    ${styles.bodyBold}
  }
`;

// @ts-expect-error FIXME: generic type requires one argument
type styleOverridesType = { [k: string]: Interpolation };

type RenderParams = {
  textColor?: string;
  styleOverrides?: styleOverridesType;
  skipWrapper?: boolean;
  useBuiltInMargins?: boolean;
  withCheckmark?: boolean;
  renderText?: (text: string) => ReactNode;
  heading2TagOverride?: string;
  linkColor?: string;
  underlineLinks?: boolean;
};

const generateRenderOptions = ({
  textColor,
  styleOverrides = {},
  skipWrapper,
  useBuiltInMargins,
  withCheckmark,
  renderText = (text) => text,
  heading2TagOverride,
  linkColor,
  underlineLinks,
}: RenderParams): Options => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      if (
        node.content &&
        node.content.length === 1 &&
        isText(node.content[0]) &&
        node.content[0].value === ''
      ) {
        return <br />;
      }
      if (skipWrapper) {
        return children;
      }
      return (
        <Typography
          css={[boldCss, styleOverrides[BLOCKS.PARAGRAPH]]}
          color={textColor}
          useBuiltInMargins={useBuiltInMargins}
        >
          {children}
        </Typography>
      );
    },
    [BLOCKS.HEADING_1]: (node, children) => (
      <Typography
        variant="titleDisplay"
        tag={'h1'}
        color={textColor}
        useBuiltInMargins={useBuiltInMargins}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <Typography
        variant="title"
        tag={heading2TagOverride}
        color={textColor}
        useBuiltInMargins={useBuiltInMargins}
      >
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Typography variant="titleH3" color={textColor} useBuiltInMargins={useBuiltInMargins}>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <Typography variant="titleH4" color={textColor} useBuiltInMargins={useBuiltInMargins}>
        {children}
      </Typography>
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <Typography variant="preamble" color={textColor} useBuiltInMargins={useBuiltInMargins}>
        {children}
      </Typography>
    ),
    [BLOCKS.UL_LIST]: (node, children) => (
      <ul
        css={[
          css`
            padding-inline-start: ${spacing[4]}px;
          `,
          styleOverrides[BLOCKS.UL_LIST],
        ]}
      >
        {children}
      </ul>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => (
      <li
        css={[
          withCheckmark &&
            css`
              display: flex;
              list-style-type: none;
              :before {
                content: '\\2713';
                margin-right: ${spacing[2]}px;
              }
            `,
          styleOverrides[BLOCKS.LIST_ITEM],
        ]}
      >
        {children}
      </li>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { file, title } = node.data.target.fields;
      const { contentType, url } = file;
      if (contentType.startsWith('image/')) {
        return <InlineImage src={`https:${url}?w=1080`} alt={title} />;
      } else {
        return (
          <>
            <Typography
              tag="a"
              href={`https:${url}`}
              target="_blank"
              rel="noreferrer noopener"
              download
              useBuiltInMargins={useBuiltInMargins}
            >
              <Icon
                name="file-text"
                css={css`
                  position: relative;
                  top: 2px;
                  margin-right: ${spacing[1]}px;
                `}
              />
              {title}
            </Typography>
          </>
        );
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      return handleEntry({
        entry: node.data.target,
        color: textColor,
        linkColor: linkColor,
        underlineLinks: underlineLinks,
      });
    },
    [INLINES.ENTRY_HYPERLINK]: (node) => {
      const { slug, title } = node.data.target.fields;
      return StaticPageLink({ slug, title });
    },
    [INLINES.HYPERLINK]: (node) => {
      const isLocalLink = node.data.uri && node.data.uri.startsWith('/');
      const content = (node.content[0] as Text).value;
      return (
        <span
          css={css`
            > * {
              color: ${textColor};
            }
          `}
        >
          {isLocalLink ? (
            <Link href={node.data.uri}>
              <StyledLink linkColor={linkColor} underlineLinks={underlineLinks}>
                {content}
              </StyledLink>
            </Link>
          ) : (
            <a href={node.data.uri}>{content}</a>
          )}
        </span>
      );
    },
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      return handleEntry({
        entry: node.data.target,
        isInline: true,
        color: textColor,
        linkColor: linkColor,
        underlineLinks: underlineLinks,
      });
    },
  },
  renderText(text) {
    if (renderText) return renderText(text);

    return text;
  },
});

function handleEntry<T>({
  entry,
  isInline,
  color,
  linkColor,
  underlineLinks,
}: {
  entry: Entry<T>;
  isInline?: boolean;
  color?: string;
  linkColor?: string;
  underlineLinks?: boolean;
}) {
  if (entry.sys.contentType.sys.id === 'responsiveBlock') {
    const { header, image, text } = entry.fields as Partial<{
      header: string;
      text: string;
      image: Asset;
      direction: string;
    }>;
    return (
      <ResponsiveBlock
        header={header}
        image={image}
        text={text}
        direction={responsiveBlockIndex++ % 2 === 0 ? 'row' : 'row-reverse'}
      />
    );
  }
  if (!isScribble(entry)) {
    return null;
  }
  const { text, richText, variants = ['body'], color: scribbleColor } = entry.fields;
  if (!text && !richText) {
    return null;
  }
  const appliedColor = (scribbleColor && colors[scribbleColor]) || color;
  return variants.reduce<JSX.Element | string>(
    (component, variant: string) => {
      return (
        <Scribble
          variant={variant as ScribbleVariants}
          tag={isInline ? 'span' : undefined}
          color={appliedColor}
        >
          {component}
        </Scribble>
      );
    },
    richText ? (
      <RichText
        document={richText}
        skipWrapper
        textColor={appliedColor}
        linkColor={linkColor}
        underlineLinks={underlineLinks}
      />
    ) : (
      (text as string)
    ),
  );
}

const isScribble = (entry: Entry<unknown>): entry is IScribble =>
  entry.sys.contentType.sys.id === 'scribble';

const isText = (node: Block | Inline | Text): node is Text => (node as Text).value !== undefined;

export type RichTextProps = {
  document?: Document;
  centered?: boolean;
  textColor?: string;
  styleOverrides?: styleOverridesType;
  className?: string;
  skipWrapper?: boolean;
  useBuiltInMargins?: boolean;
  withCheckmark?: boolean;
  heading2TagOverride?: string;
  linkColor?: string;
  underlineLinks?: boolean;
};

const RichText = ({
  document,
  centered,
  textColor,
  styleOverrides,
  className,
  skipWrapper,
  useBuiltInMargins,
  withCheckmark,
  heading2TagOverride,
  linkColor,
  underlineLinks,
}: RichTextProps) => {
  const { templateReplacer } = useContext(TemplatingContext);
  if (!document) {
    return null;
  }
  const renderOptions = generateRenderOptions({
    textColor,
    styleOverrides,
    skipWrapper,
    useBuiltInMargins,
    withCheckmark,
    renderText: templateReplacer,
    heading2TagOverride,
    linkColor,
    underlineLinks,
  });

  const documentElement = documentToReactComponents(document, renderOptions);
  if (skipWrapper) {
    return documentElement as JSX.Element;
  }
  return (
    <Typography
      tag="div"
      className={className}
      css={css`
        position: relative;
        z-index: 0;
        white-space: pre-wrap;
        text-align: ${centered ? 'center' : 'initial'};
        color: ${textColor};
        br:last-child {
          display: none;
        }
      `} // https://github.com/contentful/rich-text/issues/101
      useBuiltInMargins={useBuiltInMargins}
    >
      {documentElement}
    </Typography>
  );
};

export default React.memo(RichText);
